import * as Popover from "@radix-ui/react-popover";
import { Calendar, Check, ChevronDown, X } from "lucide-react";
import * as React from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";

/**
 * Filter chips (MV2-841, ds/Filters).
 *
 * A chip is a filter you can see the worth of before pressing: it ALWAYS
 * carries its count, computed from the same response as the rows — never a
 * second query. On, it takes the action colour at 12% with a 45% border.
 * State is in the URL (`lib/urlState.ts`), so the row is a shared view.
 *
 * A chip the persona cannot use is disabled with its reason (dashed border,
 * 45% opacity, reason as tooltip), never hidden.
 *
 * `DropChip`, `ChipSelect` and `DateRangeChip` are the chips that OPEN
 * something (a menu, a date popover) — promoted here from the Audit log and
 * MLOps Runs pages, which drew them markup-for-markup (MV2-915 follow-up).
 */

/** The chip's "on" recipe — action 12% fill, 45% border (ds/Filters). */
export const CHIP_ON =
  "border-[color:color-mix(in_srgb,var(--action)_45%,transparent)] bg-[color:color-mix(in_srgb,var(--action)_12%,transparent)] text-gray-900";

export interface FilterChipProps {
  label: React.ReactNode;
  count?: number;
  on?: boolean;
  onClick?: () => void;
  /** A verdict dot colour — `var(--success)`. */
  dot?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Required when `disabled`. */
  reason?: string;
  className?: string;
}

export function FilterChip({
  label,
  count,
  on = false,
  onClick,
  dot,
  icon,
  disabled = false,
  reason,
  className,
}: FilterChipProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      title={disabled ? reason : undefined}
      className={cn(
        "inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] py-1 text-[12px] font-medium outline-none",
        "focus-visible:ring-2 focus-visible:ring-focus",
        on ? CHIP_ON : "border-gray-200 bg-surface text-gray-600 hover:bg-gray-50",
        disabled &&
          "cursor-not-allowed border-dashed opacity-45 hover:bg-surface",
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden
          className="inline-block h-[7px] w-[7px] flex-none rounded-full"
          style={{ background: dot }}
        />
      )}
      {icon}
      {label}
      {count !== undefined && (
        <span className="tabular text-[11px] text-gray-500">{count}</span>
      )}
    </button>
  );
}

/** The wrapping row a chip set lives in; `end` is the slot after the spacer (search). */
export function FilterChipRow({
  children,
  end,
  className,
}: {
  children: React.ReactNode;
  end?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-[7px]", className)}
      role="group"
    >
      {children}
      {end && <div className="ml-auto flex flex-wrap items-center gap-[7px]">{end}</div>}
    </div>
  );
}

export interface AppliedFilter {
  label: string;
  onRemove: () => void;
}

/**
 * When more than two filters are on they collapse into one summary row —
 * "Showing 5 of 33 · Offline × Degraded × MQTT × … Clear all".
 */
export function AppliedFilterBar({
  shown,
  total,
  filters,
  onClearAll,
  className,
}: {
  shown: number;
  total: number;
  filters: AppliedFilter[];
  onClearAll: () => void;
  className?: string;
}): React.ReactElement | null {
  if (filters.length === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-[8px] border border-gray-200 px-[11px] py-[9px]",
        className,
      )}
    >
      <span className="text-[11px] text-gray-500">Showing</span>
      <span className="tabular text-[14px] font-semibold text-gray-900">
        {shown}
      </span>
      <span className="text-[11px] text-gray-500">of {total} ·</span>
      {filters.map((f) => (
        <button
          key={f.label}
          type="button"
          onClick={f.onRemove}
          aria-label={`Remove filter ${f.label}`}
          className="inline-flex items-center gap-1 rounded-full border border-[color:color-mix(in_srgb,var(--action)_45%,transparent)] bg-[color:color-mix(in_srgb,var(--action)_12%,transparent)] px-[9px] py-0.5 text-[11px] font-medium text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          {f.label}
          <X size={11} aria-hidden />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="ml-auto inline-flex h-[26px] items-center rounded-[6px] px-[9px] text-[12px] font-medium text-gray-600 hover:bg-gray-100"
      >
        Clear all
      </button>
    </div>
  );
}

/* ─────────────────────── Chips that open something ─────────────────────── */

/**
 * A chip that opens something. `FilterChip` is a leaf without a forwarded
 * ref, so it cannot be a Radix trigger; this is the same anatomy (the
 * ds/Filters recipe, disabled = dashed + reason as the tooltip) on a
 * ref-forwarding button.
 */
export const DropChip = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { on?: boolean; reason?: string }
>(({ on, reason, disabled, className, children, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-pressed={on}
    aria-disabled={disabled || undefined}
    disabled={disabled}
    title={disabled ? reason : rest.title}
    className={cn(
      "inline-flex h-7 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] text-[12px] font-medium outline-none",
      "focus-visible:ring-2 focus-visible:ring-focus",
      on ? CHIP_ON : "border-gray-200 bg-surface text-gray-600 hover:bg-gray-50",
      disabled && "cursor-not-allowed border-dashed opacity-45 hover:bg-surface",
      className,
    )}
    {...rest}
  >
    {children}
  </button>
));
DropChip.displayName = "DropChip";

export interface ChipSelectOption<T extends string = string> {
  value: T;
  label: string;
  /** Rides along on the menu row, tabular — from the same response as the rows. */
  count?: number;
}

export interface ChipSelectProps<T extends string> {
  /** `Category`, `Type` — the chip reads `{prefix} · {current}`. */
  prefix: string;
  value: T | null;
  onChange: (v: T | null) => void;
  options: ReadonlyArray<ChipSelectOption<T>>;
  /** The cleared state's word — `any` (default), `All`. Always the first menu item. */
  anyLabel?: string;
  /** Filled state; defaults to "a value is picked". Pass `on` for a chip the board draws filled at rest. */
  on?: boolean;
  disabled?: boolean;
  /** Required when `disabled`. */
  reason?: string;
  /** Option labels are identifiers (audit keys) — set in mono. */
  mono?: boolean;
  /** Shown under the clear item when there are no options. */
  emptyLabel?: React.ReactNode;
  /** Menu overrides, e.g. `min-w-[180px]`. */
  contentClassName?: string;
}

/** `Category · any ⌄` — a dropdown chip; the first item clears. */
export function ChipSelect<T extends string>({
  prefix,
  value,
  onChange,
  options,
  anyLabel = "any",
  on,
  disabled,
  reason,
  mono = false,
  emptyLabel,
  contentClassName,
}: ChipSelectProps<T>): React.ReactElement {
  const current = value ? (options.find((o) => o.value === value)?.label ?? value) : anyLabel;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <DropChip on={on ?? !!value} disabled={disabled} reason={reason} aria-label={`${prefix} filter`}>
          {prefix} · {current} <ChevronDown size={11} aria-hidden />
        </DropChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn("max-h-[320px] min-w-[200px] overflow-y-auto", contentClassName)}
      >
        <DropdownMenuItem onSelect={() => onChange(null)} className="justify-between gap-3">
          <span>{anyLabel}</span>
          {value === null ? <Check size={12} aria-hidden /> : null}
        </DropdownMenuItem>
        {options.length === 0 && emptyLabel !== undefined ? (
          <div className="px-[9px] py-1.5 text-[11px] text-gray-500">{emptyLabel}</div>
        ) : null}
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onSelect={() => onChange(o.value)} className="justify-between gap-3">
            <span className={mono ? "font-mono text-[12px]" : undefined}>{o.label}</span>
            <span className="inline-flex items-center gap-2">
              {o.count !== undefined ? <span className="tabular text-[11px] text-gray-500">{o.count}</span> : null}
              {o.value === value ? <Check size={12} aria-hidden /> : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface DateRangeChipProps {
  /** `YYYY-MM-DD` UTC calendar days, inclusive. */
  from: string;
  to: string;
  /** The chip's face — `29 Aug → 5 Sep`; the caller formats (page-specific year rules). */
  label: React.ReactNode;
  /** Latest selectable day (`YYYY-MM-DD`). */
  max?: string;
  /** The popover's last line. */
  note?: string;
  /** Fires on every input change, including a cleared input (`""`) — guard if the page needs a value. */
  onChange: (from: string, to: string) => void;
}

const DATE_INPUT =
  "tabular rounded-[6px] border border-gray-300 bg-surface px-2 py-1 text-[12px] text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus";

/** `📅 29 Aug → 5 Sep` — opens two UTC date inputs. */
export function DateRangeChip({
  from,
  to,
  label,
  max,
  note = "Whole days, midnight to midnight UTC.",
  onChange,
}: DateRangeChipProps): React.ReactElement {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <DropChip aria-label="Date range (UTC)">
          <Calendar size={11} aria-hidden /> {label}
        </DropChip>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 flex flex-col gap-2 rounded-[10px] border border-gray-200 bg-[color:var(--gray-0)] p-3 text-[12px] text-gray-800 shadow-md"
        >
          <label className="flex items-center justify-between gap-3">
            <span className="text-gray-500">From · UTC</span>
            <input
              type="date"
              value={from}
              max={to || max}
              onChange={(e) => onChange(e.target.value, to)}
              className={DATE_INPUT}
            />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-gray-500">To · UTC</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              max={max}
              onChange={(e) => onChange(from, e.target.value)}
              className={DATE_INPUT}
            />
          </label>
          <span className="text-[11px] text-gray-500">{note}</span>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
